// Typed errors so callers never branch on `Success` / status codes themselves.
// The four-token contract maps codes to recovery (see authClient):
//   428 stale request token / 409 replay  -> refresh + retry  (StaleTokenError)
//   401 no session / refresh denied        -> terminal logout  (UnauthorizedError)
//   403 authenticated but lacks permission -> surface as-is    (ForbiddenError)

import type { ApiErrorBody } from './types'

/** Any non-successful Yoreflix API response, surfaced as a thrown error. */
export class ApiError extends Error {
  readonly code: number
  readonly description: string

  constructor(error: ApiErrorBody) {
    super(`Yoreflix API error ${error.Code}: ${error.Description}`)
    this.name = 'ApiError'
    this.code = error.Code
    this.description = error.Description
  }
}

/**
 * Recoverable token failure (428 stale request token, or 409 replay). The
 * session is still live; the single-use RequestToken/CSRF could not be validated
 * (rotation race past the grace window, a dropped response, a replay). The client
 * refreshes via the CredentialsToken and retries — it does NOT mean "logged out".
 */
export class StaleTokenError extends ApiError {
  constructor(error: ApiErrorBody) {
    super(error)
    this.name = 'StaleTokenError'
  }
}

/** No live session / refresh denied (401). Terminal — drop to the sign-in screen. */
export class UnauthorizedError extends ApiError {
  constructor(error: ApiErrorBody) {
    super(error)
    this.name = 'UnauthorizedError'
  }
}

/** Authenticated, but the account lacks the required permission (403). */
export class ForbiddenError extends ApiError {
  constructor(error: ApiErrorBody) {
    super(error)
    this.name = 'ForbiddenError'
  }
}

/**
 * The credentials were RIGHT, and the account had a deletion booked whose date had come — so
 * signing in is what carried it out. There is no session, and there is no account either.
 *
 * ⛔ IT IS NOT AN `UnauthorizedError`, AND THE DIFFERENCE IS THE WHOLE POINT. The server answers
 * this as a 200 with `AccountDeleted` precisely so it cannot be confused with a bad password; a
 * client that folded it back into "unauthorised" would put somebody who deliberately deleted their
 * account into a password-reset loop for a row that no longer exists. Callers send them to signup.
 *
 * ⚠ Not an ApiError: nothing failed. It carries the server's own sentence rather than a code.
 */
export class AccountDeletedError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AccountDeletedError'
  }
}
